/**
 * Finnish capital income tax (pääomatulovero) on dividends and capital gains, 2026 rules.
 *
 * Sources:
 *  - Verohallinto, "Pääomatulot": capital income is taxed at 30% up to EUR 30,000 of taxable
 *    capital income per year and 34% on the part above that.
 *  - Verohallinto, "Osingot listatusta yhtiöstä": dividends from publicly listed companies are
 *    85% taxable capital income and 15% tax-exempt.
 *  - Capital gains (luovutusvoitot) are taxable capital income in full; the caller enters the
 *    gain net of acquisition cost (or the deemed acquisition cost).
 *  - Verohallinto, "Osingot listaamattomasta yhtiöstä": dividends from an unlisted company are
 *    split by the share's mathematical value (the company's net assets per share). Up to an 8%
 *    annual yield on that value, 25% of the dividend is taxable capital income (75% tax-free)
 *    for the first EUR 150,000 per year and 85% above that; the part of the dividend exceeding
 *    the 8% yield is 75% taxable EARNED income (25% tax-free), taxed with the salary. The caller
 *    routes `unlistedEarnedIncomePortion` into the salary engine as other earned income. When the
 *    net assets are unknown, the whole dividend is assumed to stay within the 8% yield.
 *
 * Deliberately out of scope: the company's own 20% corporate tax before the dividend, voluntary
 * pension contributions, capital income deductions (interest on income-producing debt), and
 * losses carried forward. Those need facts a salary calculator has no business asking.
 */

export interface CapitalIncomeParams {
  year: number;
  lowerRate: number;
  upperRate: number;
  upperRateThreshold: number; // EUR/year of taxable capital income
  listedDividendTaxableShare: number;
  /** Unlisted-company dividends: the yield on the share's mathematical value that counts as
   *  "lightly taxed", the taxable shares below and above the euro cap, and the earned-income
   *  share of anything beyond the yield. */
  unlistedYieldRate: number;
  unlistedLightTaxableShare: number;
  unlistedLightCap: number;
  unlistedHeavyTaxableShare: number;
  unlistedExcessEarnedShare: number;
}

export const CAPITAL_INCOME_PARAMS_2026: CapitalIncomeParams = {
  year: 2026,
  lowerRate: 0.3,
  upperRate: 0.34,
  upperRateThreshold: 30_000,
  listedDividendTaxableShare: 0.85,
  unlistedYieldRate: 0.08,
  unlistedLightTaxableShare: 0.25,
  unlistedLightCap: 150_000,
  unlistedHeavyTaxableShare: 0.85,
  unlistedExcessEarnedShare: 0.75,
};

export interface CapitalIncomeInput {
  /** Dividends from publicly listed companies, EUR/year, gross. */
  dividends?: number;
  /** Capital gains net of acquisition cost, EUR/year. */
  capitalGains?: number;
  /** Dividends from an unlisted company the person owns shares in, EUR/year, gross. */
  unlistedDividends?: number;
  /** The mathematical value of those shares (the person's share of the company's net assets),
   *  EUR. Sets the 8% yield limit. Omit when unknown: the dividend is then assumed to stay
   *  within the limit. */
  unlistedNetAssets?: number;
  params?: CapitalIncomeParams;
}

export interface CapitalIncomeResult {
  dividends: number;
  capitalGains: number;
  unlistedDividends: number;
  /** How the unlisted dividend was split: within the 8% yield below and above the EUR 150,000
   *  cap, and the part beyond the yield that is taxed as earned income. */
  unlisted: { light: number; heavy: number; excess: number };
  /** The taxable earned-income part of the unlisted dividend (75% of the excess). The salary
   *  engine taxes it; it is NOT in `net` here. */
  unlistedEarnedIncomePortion: number;
  /** All capital-type income before tax: listed dividends + gains + unlisted dividends. */
  gross: number;
  taxableCapitalIncome: number;
  tax: number;
  /** gross minus capital income tax minus the earned-income portion routed to the salary engine. */
  net: number;
  /** tax / gross, as a percentage (0 when there is no capital income). */
  effectiveRatePct: number;
}

export function calculateCapitalIncome(input: CapitalIncomeInput): CapitalIncomeResult {
  const p = input.params ?? CAPITAL_INCOME_PARAMS_2026;
  const dividends = Math.max(0, input.dividends ?? 0);
  const capitalGains = Math.max(0, input.capitalGains ?? 0);
  const unlistedDividends = Math.max(0, input.unlistedDividends ?? 0);
  const yieldLimit = input.unlistedNetAssets !== undefined && input.unlistedNetAssets > 0 ? input.unlistedNetAssets * p.unlistedYieldRate : Infinity;
  const withinYield = Math.min(unlistedDividends, yieldLimit);
  const light = Math.min(withinYield, p.unlistedLightCap);
  const heavy = withinYield - light;
  const excess = unlistedDividends - withinYield;
  const unlistedEarnedIncomePortion = excess * p.unlistedExcessEarnedShare;
  const gross = dividends + capitalGains + unlistedDividends;
  const taxable = dividends * p.listedDividendTaxableShare + capitalGains + light * p.unlistedLightTaxableShare + heavy * p.unlistedHeavyTaxableShare;
  const lower = Math.min(taxable, p.upperRateThreshold);
  const upper = Math.max(0, taxable - p.upperRateThreshold);
  const tax = lower * p.lowerRate + upper * p.upperRate;
  const r = (x: number) => Math.round(x * 100) / 100;
  return {
    dividends: r(dividends),
    capitalGains: r(capitalGains),
    unlistedDividends: r(unlistedDividends),
    unlisted: { light: r(light), heavy: r(heavy), excess: r(excess) },
    unlistedEarnedIncomePortion: r(unlistedEarnedIncomePortion),
    gross: r(gross),
    taxableCapitalIncome: r(taxable),
    tax: r(tax),
    net: r(gross - tax - unlistedEarnedIncomePortion),
    effectiveRatePct: gross > 0 ? r((tax / gross) * 100) : 0,
  };
}
